import type { User } from '../../lib/types'

export type InventoryTab = 'history' | 'handover'

const INVENTORY_TABS: readonly InventoryTab[] = ['history', 'handover']

export function resolveInventoryTab(requested: string | null | undefined): InventoryTab {
  return INVENTORY_TABS.find((tab) => tab === requested) ?? INVENTORY_TABS[0]
}

export type InventoryView = 'stock' | 'fixed' | 'laptops' | 'accounts' | 'office' | 'field' | 'people' | 'requests' | 'oreas' | 'appoint'

export const INVENTORY_VIEWS: readonly InventoryView[] = [
  'stock',
  'fixed',
  'people',
  'office',
  'laptops',
  'accounts',
  'field',
  'requests',
]

export type InventoryTick = 'assets' | 'stock' | 'accounts' | 'oreas'

export const INVENTORY_TICKS: readonly InventoryTick[] = ['assets', 'stock', 'accounts', 'oreas']

/** Held today as the server counts it: the admin switch, their own ticks and a live temp rep's. */
export function holdsTick(user: User | undefined | null, kind: InventoryTick): boolean {
  return user?.inventory_ticks?.includes(kind) === true
}

export function readsInventory(user: User | undefined | null): boolean {
  return user?.inventory_reader === true
}

const TICK_OF_VIEW: Partial<Record<InventoryView, InventoryTick>> = {
  stock: 'stock',
  laptops: 'assets',
  people: 'assets',
  office: 'assets',
  field: 'assets',
  accounts: 'accounts',
  oreas: 'oreas',
}

export function mayEditInventoryView(user: User | undefined | null, view: InventoryView): boolean {
  const tick = TICK_OF_VIEW[view]
  return tick !== undefined && holdsTick(user, tick)
}

export interface InventoryViewAccess {
  reader: boolean
  answersRequests: boolean
  admin: boolean
}

export function inventoryViewAccess(user: User | undefined | null): InventoryViewAccess {
  return {
    reader: readsInventory(user),
    answersRequests: holdsTick(user, 'assets') || holdsTick(user, 'accounts'),
    admin: user?.is_admin === true,
  }
}

/** Every tab reads for a reader; the queue is for whoever answers a kind in it and Appoint is the admin's. */
export function visibleInventoryViews({ reader, answersRequests, admin }: InventoryViewAccess): readonly InventoryView[] {
  if (!reader) return []
  const views = INVENTORY_VIEWS.filter((view) => view !== 'requests' || answersRequests)
  return admin ? [...views, 'oreas', 'appoint'] : [...views, 'oreas']
}

export function resolveInventoryView(
  requested: string | null | undefined,
  access: InventoryViewAccess,
): InventoryView {
  const views = visibleInventoryViews(access)
  return views.find((view) => view === requested) ?? views[0] ?? 'stock'
}

export function inventoryViewLabelKey(view: InventoryView): string {
  if (view === 'oreas') return 'oreas.tab'
  return view === 'requests' ? 'equipmentRequest.view' : `inventory.register.view.${view}`
}

export function inventoryViewHelpKey(view: InventoryView): string {
  if (view === 'oreas') return 'oreas.help'
  return view === 'requests' ? 'equipmentRequest.viewHelp' : `inventory.register.help.${view}`
}

/** The query string for another view: the laptop detail tab and the picked person belong to their own view. */
export function inventoryViewSearch(current: string, view: InventoryView, personId?: string): string {
  const params = new URLSearchParams(current)
  params.delete('tab')
  params.delete('person')
  params.set('view', view)
  if (personId) params.set('person', personId)
  return `?${params.toString()}`
}
